import React, { useState, useEffect } from 'react';
import { api } from '../../api/nocode';
import { sendEmail } from '../../api/ses';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';

const STAGE_VALUES = {
  'idea': 1,
  'draft': 2,
  'rough': 3,
  'ready': 4,
  'published': 5
};

const getStageVal = (stage) => STAGE_VALUES[stage?.toLowerCase()?.split('_')[0]] || 0;

export default function Bunkmates() {
  const [users, setUsers] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [proposedPairs, setProposedPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState('');
  
  // Test Controls
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Manual selection (up to 3 users)
  const [selectedForManual, setSelectedForManual] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, pairsRes] = await Promise.all([
        api.get('users_gwacamp'),
        api.get('bunkmate_pairs_fbwm')
      ]);
      
      const allUsers = Array.isArray(usersRes) ? usersRes : usersRes.data || [];
      const allPairs = Array.isArray(pairsRes) ? pairsRes : pairsRes.data || [];
      
      setUsers(allUsers);
      setPairs(allPairs);
    } catch (e) {
      console.error("Error fetching data", e);
      showToast("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // Derived state: Filter unpaired users to only those with pass_type="allaccess" AND wants_bunkmate = 1 (true)
  const allAccessUsers = users.filter(u => u.pass_type === 'allaccess' && (u.wants_bunkmate === 1 || u.wants_bunkmate === true));
  
  const unpairedUsers = allAccessUsers.filter(u => {
    const isPairedInDb = pairs.some(p => 
      String(p.user_a_id) === String(u.id) || 
      String(p.user_b_id) === String(u.id) || 
      (p.user_c_id && String(p.user_c_id) === String(u.id))
    );
    const isProposed = proposedPairs.some(p => 
      p.user1.id === u.id || 
      p.user2.id === u.id || 
      (p.user3 && p.user3.id === u.id)
    );
    return !isPairedInDb && !isProposed;
  });

  const confirmedPairsData = pairs.map(p => {
    const u1 = users.find(u => String(u.id) === String(p.user_a_id));
    const u2 = users.find(u => String(u.id) === String(p.user_b_id));
    const u3 = p.user_c_id ? users.find(u => String(u.id) === String(p.user_c_id)) : null;
    return { ...p, user1: u1, user2: u2, user3: u3 };
  }).filter(p => p.user1 && p.user2);

  const runAutoPair = () => {
    let pools = {
      male: { fiction: [], nonfiction: [], other: [] },
      female: { fiction: [], nonfiction: [], other: [] },
      other: [] // Prefer not to say / Unspecified
    };

    // 1. Distribute into pools
    unpairedUsers.forEach(u => {
      const gender = u.gender?.toLowerCase() || 'other';
      const genre = u.genre?.toLowerCase() || 'other';

      if (gender === 'male' || gender === 'female') {
        if (genre === 'fiction' || genre === 'nonfiction') {
          pools[gender][genre].push(u);
        } else {
          pools[gender].other.push(u);
        }
      } else {
        pools.other.push(u);
      }
    });

    let newPairs = [];
    let leftovers = [];

    // Helper to pair within a specific array based on journey stage proximity
    const pairArray = (arr) => {
      arr.sort((a, b) => getStageVal(a.journey_stage) - getStageVal(b.journey_stage));
      while (arr.length >= 2) {
        newPairs.push({
          user1: arr.shift(),
          user2: arr.shift()
        });
      }
      if (arr.length === 1) leftovers.push(arr[0]);
    };

    // 2. Pair exact matches (Gender -> Genre -> Stage)
    ['male', 'female'].forEach(gender => {
      ['fiction', 'nonfiction'].forEach(genre => {
        pairArray(pools[gender][genre]);
      });
      // Merge genre leftovers with gender 'other/unspecified genre'
      let genderLeftovers = [...leftovers, ...pools[gender].other];
      leftovers = []; // reset
      pairArray(genderLeftovers);
    });

    // 3. Merge all remaining leftovers and 'other/prefer not to say' gender
    let finalPool = [...leftovers, ...pools.other];
    pairArray(finalPool);

    setProposedPairs(newPairs);
    if (newPairs.length > 0) {
      showToast(`Generated ${newPairs.length} proposed pairs.`);
    } else {
      showToast("Not enough unpaired users to generate pairs.");
    }
  };

  const handleDragStart = (e, pairIndex, userKey) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ pairIndex, userKey }));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = (e, targetPairIndex, targetUserKey) => {
    e.preventDefault();
    try {
      const source = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (source.pairIndex === targetPairIndex && source.userKey === targetUserKey) return;
      
      const newPairs = [...proposedPairs];
      const temp = newPairs[source.pairIndex][source.userKey];
      newPairs[source.pairIndex][source.userKey] = newPairs[targetPairIndex][targetUserKey];
      newPairs[targetPairIndex][targetUserKey] = temp;
      
      setProposedPairs(newPairs);
    } catch (err) {
      console.error("Drop failed", err);
    }
  };

  const confirmPairs = async (pairsToConfirm) => {
    setProcessing(true);
    let successCount = 0;

    for (const pair of pairsToConfirm) {
      try {
        const payload = {
          user_a_id: String(pair.user1.id),
          user_b_id: String(pair.user2.id),
          user_c_id: pair.user3 ? String(pair.user3.id) : null,
          status: 'paired',
          owner_id: 'admin'
        };

        // 1. Create Pair Record
        await api.post('bunkmate_pairs_fbwm', payload);

        // 2. Update Users
        await api.put('users_gwacamp', pair.user1.id, { bunkmate_id: String(pair.user2.id) });
        await api.put('users_gwacamp', pair.user2.id, { bunkmate_id: String(pair.user1.id) });
        if (pair.user3) {
          await api.put('users_gwacamp', pair.user3.id, { bunkmate_id: String(pair.user1.id) });
        }

        successCount++;
      } catch (err) {
        console.error("Failed to save pair:", pair, err);
      }
    }

    showToast(`Successfully confirmed ${successCount} pairs.`);
    setProposedPairs([]);
    setSelectedForManual([]);
    fetchData();
    setProcessing(false);
  };

  const handleManualPair = () => {
    if (selectedForManual.length < 2 || selectedForManual.length > 3) return;
    const u1 = unpairedUsers.find(u => u.id === selectedForManual[0]);
    const u2 = unpairedUsers.find(u => u.id === selectedForManual[1]);
    const u3 = selectedForManual.length === 3 ? unpairedUsers.find(u => u.id === selectedForManual[2]) : null;
    confirmPairs([{ user1: u1, user2: u2, user3: u3 }]);
  };

  const handleRemovePair = async (pairId) => {
    if (!window.confirm("Are you sure you want to remove this pair? This will unpair the users and let them be paired again.")) return;
    setProcessing(true);
    try {
      const pair = pairs.find(p => p.id === pairId);
      if (pair) {
        // Clear bunkmate IDs on user profiles
        if (pair.user_a_id) await api.put('users_gwacamp', pair.user_a_id, { bunkmate_id: null });
        if (pair.user_b_id) await api.put('users_gwacamp', pair.user_b_id, { bunkmate_id: null });
        if (pair.user_c_id) await api.put('users_gwacamp', pair.user_c_id, { bunkmate_id: null });
        
        // Delete pair record
        await api.delete('bunkmate_pairs_fbwm', pairId);
        showToast("Pair deleted successfully.");
        fetchData();
      }
    } catch (err) {
      console.error("Failed to delete pair", err);
      showToast("Error deleting pair.");
    } finally {
      setProcessing(false);
    }
  };

  // Premium Branded Email Template
  const getEmailHtml = (recipient, partners) => {
    const subject = "Your Camp Bunkmate!";
    const partnersInfo = partners.map(p => `<strong>${p.name}</strong> (Genre: <em>${p.genre || 'various genres'}</em>, Stage: <em>${p.journey_stage?.replace('_', ' ') || 'early'}</em>)`).join(" and ");
    const partnersEmails = partners.map(p => `<a href="mailto:${p.email}" style="color: #2D5016; font-weight: bold; text-decoration: underline; font-size: 18px;">${p.email}</a>`).join("<br/>");

    const body = `
      <p style="margin-top: 0; margin-bottom: 20px;">Hello ${recipient.name},</p>
      <p style="margin-bottom: 20px;">Every camper gets a bunkmate. Someone on the same road, writing in the same world.</p>
      <p style="margin-bottom: 20px;">Your bunkmate cluster includes: ${partnersInfo}.</p>
      
      <div style="margin-bottom: 25px; background-color: #EAF1E6; padding: 20px; border-left: 4px solid #2D5016; border-radius: 0 8px 8px 0;">
        <p style="margin: 0; font-size: 16px; color: #2D5016;">
          <strong>Reach out to them at:</strong><br/>
          ${partnersEmails}
        </p>
      </div>
      
      <p style="margin-bottom: 20px;">Introduce yourself and say howdy, share a little about yourself and your own journey, and ask how you can pray for them this week — that's what camp is for!</p>
      <p style="margin-bottom: 0;">Best,<br/><strong>Team GospelWise Author Camp</strong></p>
    `;

    return `
      <div style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #fdfaf6; padding: 0;">
        <div style="text-align: center; padding: 40px 20px 20px;">
          <img src="https://www.faibowrimo.com/wp-content/uploads/2026/05/camp-logo-sm.png" alt="GospelWise Author Camp" style="max-width: 180px; height: auto;" />
        </div>
        <div style="padding: 0 40px 20px;">
          <h2 style="color: #2D5016; margin-bottom: 25px; font-size: 24px; text-align: center;">${subject}</h2>
          
          <div style="font-size: 17px; line-height: 1.8; background: #ffffff; padding: 40px; border-top: 4px solid #D4A373; border-radius: 8px; color: #2c3e20; box-shadow: 0 4px 15px rgba(0,0,0,0.03);">
            ${body}
          </div>
          
          <p style="margin-top: 40px; text-align: center; font-size: 12px; color: #a39b90;">
            © 2026 Gospel-Wise Author. All rights reserved.
          </p>
        </div>
      </div>
    `;
  };

  const sendNotifications = async () => {
    if (!window.confirm("Are you sure you want to send emails to all unnotified pairs?")) return;
    
    setProcessing(true);
    let sentCount = 0;
    const unnotifiedPairs = confirmedPairsData.filter(p => p.status !== 'notified');

    for (const pair of unnotifiedPairs) {
      const { user1, user2, user3 } = pair;

      try {
        // User 1 receives User 2 & User 3 details
        const u1Partners = [user2];
        if (user3) u1Partners.push(user3);
        await sendEmail(user1.email, "Your Camp Bunkmate!", getEmailHtml(user1, u1Partners));

        // User 2 receives User 1 & User 3 details
        const u2Partners = [user1];
        if (user3) u2Partners.push(user3);
        await sendEmail(user2.email, "Your Camp Bunkmate!", getEmailHtml(user2, u2Partners));

        // User 3 receives User 1 & User 2 details
        if (user3) {
          const u3Partners = [user1, user2];
          await sendEmail(user3.email, "Your Camp Bunkmate!", getEmailHtml(user3, u3Partners));
          sentCount++;
        }

        await api.put('bunkmate_pairs_fbwm', pair.id, { status: 'notified' });
        sentCount += 2;
      } catch (err) {
        console.error(`Failed to notify pair ${pair.id}`, err);
      }
    }

    showToast(`Sent ${sentCount} notifications.`);
    fetchData();
    setProcessing(false);
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      alert("Please enter a valid test email address.");
      return;
    }
    
    setIsSendingTest(true);
    const mockRecipient = { name: "Test Camper" };
    const mockPartner = { 
        name: "Jane Doe", 
        genre: "fiction", 
        journey_stage: "draft", 
        email: "jane.doe@example.com" 
    };
    
    const htmlBody = getEmailHtml(mockRecipient, [mockPartner]);
    
    try {
      const success = await sendEmail(testEmailAddress, "Your Camp Bunkmate! (Test)", htmlBody);
      if (success) {
        showToast(`Test email successfully delivered to ${testEmailAddress}`);
      } else {
        alert("Failed to send test email. Open your browser console for details.");
      }
    } catch (err) {
      console.error("Test email failed", err);
      alert("Failed to send test email.");
    } finally {
      setIsSendingTest(false);
    }
  };

  const toggleManualSelection = (userId) => {
    if (selectedForManual.includes(userId)) {
      setSelectedForManual(selectedForManual.filter(id => id !== userId));
    } else if (selectedForManual.length < 3) {
      setSelectedForManual([...selectedForManual, userId]);
    }
  };

  const UserCard = ({ user, draggable, onDragStart, onDrop }) => (
    <div 
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className={`p-3 border rounded-md bg-white flex flex-col gap-1 relative ${draggable ? 'cursor-grab active:cursor-grabbing hover:border-amber hover:shadow-md' : ''}`}
    >
      <div className="font-bold text-dark-green">{user.name}</div>
      <div className="text-xs text-muted capitalize flex gap-2 flex-wrap">
        <span className="bg-gray-100 px-2 py-0.5 rounded">{user.gender || 'Not Set'}</span>
        <span className="bg-gray-100 px-2 py-0.5 rounded">{user.genre || 'Not Set'}</span>
        <span className="bg-gray-100 px-2 py-0.5 rounded">{user.journey_stage?.replace('_', ' ') || 'Not Set'}</span>
      </div>
      {draggable && <SafeIcon name="Move" className="absolute top-2 right-2 text-gray-300 opacity-50" />}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Bunkmate Pairing</h1>
      </div>

      {toast && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded fixed top-4 right-4 z-50 shadow-lg animate-fadeIn">
          {toast}
        </div>
      )}

      {/* STATS */}
      <div className="grid grid-cols-3 gap-6">
        <Card className="text-center p-4">
          <div className="text-sm text-gray-500 uppercase font-bold">All-Access Campers (Requesting Bunkmate)</div>
          <div className="text-3xl font-bold text-dark-green">{allAccessUsers.length}</div>
        </Card>
        <Card className="text-center p-4">
          <div className="text-sm text-gray-500 uppercase font-bold">Paired</div>
          <div className="text-3xl font-bold text-amber">{allAccessUsers.length - unpairedUsers.length}</div>
        </Card>
        <Card className="text-center p-4">
          <div className="text-sm text-gray-500 uppercase font-bold">Unpaired</div>
          <div className="text-3xl font-bold text-red-500">{unpairedUsers.length}</div>
        </Card>
      </div>

      {/* ACTIONS */}
      <Card className="flex gap-4 items-center bg-gray-50">
        <Button 
          onClick={runAutoPair} 
          disabled={unpairedUsers.length < 2 || processing}
          className="flex-1"
        >
          <SafeIcon name="Zap" /> Run Auto-Pair Algorithm
        </Button>
        <Button 
          variant="secondary" 
          onClick={sendNotifications} 
          disabled={processing || confirmedPairsData.filter(p => p.status !== 'notified').length === 0}
          className="flex-1"
        >
          <SafeIcon name="Mail" /> Send Pending Notifications
        </Button>
      </Card>
      
      {/* TEST CONTROLS */}
      <Card className="bg-gray-50 border-dashed border-gray-300">
        <h2 className="text-lg font-bold text-dark-green mb-4 flex items-center gap-2">
          <SafeIcon name="Tool" /> Admin Test Controls
        </h2>
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <input 
            type="email" 
            className="border border-gray-300 rounded px-3 py-2 text-sm w-full sm:w-64 bg-white" 
            value={testEmailAddress} 
            onChange={e => setTestEmailAddress(e.target.value)} 
            placeholder="Recipient test email"
          />
          <Button onClick={handleTestEmail} disabled={isSendingTest} className="w-full sm:w-auto bg-gray-800 hover:bg-black text-white shrink-0">
            {isSendingTest ? 'Sending...' : 'Send Test Notification'}
          </Button>
        </div>
      </Card>

      {/* PROPOSED PAIRS (DRAFT) */}
      {proposedPairs.length > 0 && (
        <Card className="border-2 border-amber">
          <div className="flex justify-between items-center mb-4 border-b border-gray-200 pb-4">
            <div>
              <h2 className="text-lg font-bold text-dark-green flex items-center gap-2">
                <SafeIcon name="Edit3" /> Proposed Pairs ({proposedPairs.length})
              </h2>
              <p className="text-sm text-muted">Drag and drop users between cards to manually swap them before confirming.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setProposedPairs([])} disabled={processing}>Cancel</Button>
              <Button onClick={() => confirmPairs(proposedPairs)} disabled={processing}>
                {processing ? 'Saving...' : 'Confirm All Pairs'}
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {proposedPairs.map((pair, idx) => (
              <div key={idx} className="bg-gray-50 p-4 rounded-md border border-gray-200 flex items-center gap-4">
                <div className="flex-1">
                  <UserCard 
                    user={pair.user1} 
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, idx, 'user1')}
                    onDrop={(e) => handleDrop(e, idx, 'user1')}
                  />
                </div>
                <div className="text-amber"><SafeIcon name="Link" /></div>
                <div className="flex-1">
                  <UserCard 
                    user={pair.user2} 
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, idx, 'user2')}
                    onDrop={(e) => handleDrop(e, idx, 'user2')}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* UNPAIRED & MANUAL PAIRING */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 space-y-4">
          <h2 className="text-lg font-bold text-dark-green">Unpaired Users</h2>
          <p className="text-xs text-muted">Select 2 or 3 users to force a manual pair group.</p>
          
          <div className="max-h-96 overflow-y-auto space-y-2 pr-2">
            {unpairedUsers.map(u => (
              <div 
                key={u.id}
                onClick={() => toggleManualSelection(u.id)}
                className={`p-3 rounded-md border cursor-pointer transition-colors flex items-center gap-3 ${
                  selectedForManual.includes(u.id) 
                    ? 'bg-amber/10 border-amber' 
                    : 'bg-white border-gray-200 hover:border-amber/50'
                }`}
              >
                <div className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                  selectedForManual.includes(u.id) ? 'border-amber bg-amber' : 'border-gray-300'
                }`}>
                  {selectedForManual.includes(u.id) && <SafeIcon name="Check" className="text-white text-[10px]" />}
                </div>
                <div>
                  <div className="font-bold text-sm text-dark-green">{u.name}</div>
                  <div className="text-[10px] text-muted capitalize">{u.gender} • {u.genre} • {u.journey_stage}</div>
                </div>
              </div>
            ))}
            
            {loading && <div className="text-center text-sm text-muted">Loading...</div>}
            {!loading && unpairedUsers.length === 0 && <div className="text-center text-sm text-muted py-4">Everyone is paired!</div>}
          </div>

          <Button 
            className="w-full" 
            disabled={selectedForManual.length < 2 || selectedForManual.length > 3 || processing}
            onClick={handleManualPair}
          >
            Pair Selected ({selectedForManual.length}/3)
          </Button>
        </Card>

        {/* CONFIRMED PAIRS */}
        <Card className="lg:col-span-2 space-y-4">
          <h2 className="text-lg font-bold text-dark-green">Confirmed Bunkmate Pairs</h2>
          
          <div className="max-h-[500px] overflow-y-auto pr-2 space-y-3">
            {confirmedPairsData.length === 0 && !loading && (
              <div className="text-center text-muted py-10">No pairs confirmed yet.</div>
            )}
            
            {confirmedPairsData.map(pair => (
              <div key={pair.id} className="relative p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row items-center gap-4">
                <button 
                  onClick={() => handleRemovePair(pair.id)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-red-500 p-1"
                  title="Remove Pairing"
                >
                  <SafeIcon name="Trash" className="w-4 h-4" />
                </button>

                <div className="flex-1">
                  <div className="font-bold text-sm text-dark-green">{pair.user1.name}</div>
                  <div className="text-xs text-muted capitalize">{pair.user1.gender} • {pair.user1.genre} • {pair.user1.journey_stage}</div>
                </div>
                
                <div className="flex flex-col items-center justify-center px-4 shrink-0">
                  <SafeIcon name="Heart" className="text-amber mb-1" />
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                    pair.status === 'notified' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {pair.status}
                  </span>
                </div>
                
                <div className="flex-1 text-center md:text-right">
                  <div className="font-bold text-sm text-dark-green">{pair.user2.name}</div>
                  <div className="text-xs text-muted capitalize">{pair.user2.gender} • {pair.user2.genre} • {pair.user2.journey_stage}</div>
                </div>

                {pair.user3 && (
                  <>
                    <div className="hidden md:flex flex-col items-center justify-center px-2 shrink-0 text-amber">
                      <SafeIcon name="Link" />
                    </div>
                    <div className="flex-1 text-center md:text-right border-t md:border-t-0 pt-2 md:pt-0">
                      <div className="font-bold text-sm text-dark-green">{pair.user3.name}</div>
                      <div className="text-xs text-muted capitalize">{pair.user3.gender} • {pair.user3.genre} • {pair.user3.journey_stage}</div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}